// src/components/cards/shared/dialogs/convert/ConvertKennelsSection.tsx
//
// Kennel selection section for the prospect convert flow, lifted from
// BookingForm's kennels block. Each row is { kennel-type select ->
// specific kennel select -> optional heater pencil }. (+) adds a row on
// the last row; (x) removes non-first rows.
//
// Availability comes from the parent's /Booking/CheckAvailability call.
// Per-kennel heater editing uses the existing KennelHeaterManager modal.

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  SectionHeaderWrapper,
  SectionHeader,
  FieldLabelContainerMultiline,
} from '../../../../forms/shared';
import { AddRowButton, StyledIconButton } from '../../../../common/Buttons';
import CustomSelect from '../../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../../common/CustomSelectMenuItem';
import ModalBase from '../../../../common/modal/ModalBase';
import KennelHeaterManager from '../../../../forms/shared/modal/KennelHeaterManager';
import { getCustomIcon } from '../../../../../utils/customIcons';
import { KennelSideOption } from '../../../../../services/api';
import {
  getKennelsForSelection,
  getSelectedKennelIds,
  hasAvailableUnselectedKennels,
  getFirstAvailableUnselectedKennel,
  sideAcceptsPetTypes,
  selectedOptionHasKennels,
  supportsHeater,
  getKennelSidePrefix,
  Kennel,
  KennelSelection,
} from '../../../../forms/helpers/kennelHelpers';

interface ConvertKennelsSectionProps {
  kennelSelections: KennelSelection[];
  availableKennels: Kennel[];
  kennelSideOptions: KennelSideOption[];
  numberOfDays: number;
  isSubmitting: boolean;
  // Pet type ids of the pets being converted; types that accept none of
  // them are disabled. Omitted or empty means no restriction.
  petTypeIds?: number[];
  // Section-level error (e.g. "At least one kennel must be selected").
  error?: string;
  onChange: (next: KennelSelection[]) => void;
  // DOM id for scroll-to-first-error.
  id?: string;
}

const ConvertKennelsSection: React.FC<ConvertKennelsSectionProps> = ({
  kennelSelections,
  availableKennels,
  kennelSideOptions,
  numberOfDays,
  isSubmitting,
  petTypeIds = [],
  error,
  onChange,
  id,
}) => {
  // Monotonic local id generator for new rows. Kept inside this component
  // since IDs only need to be unique within the kennelSelections array.
  const [nextLocalId, setNextLocalId] = useState<number>(
    () => Math.max(0, ...kennelSelections.map((s) => s.id)) + 1
  );
  const getNextId = () => {
    const id = nextLocalId;
    setNextLocalId((n) => n + 1);
    return id;
  };

  // Per-row heater modal state. editingId === null => modal closed.
  const [editingHeaterId, setEditingHeaterId] = useState<number | null>(null);
  const editingSelection =
    editingHeaterId === null
      ? null
      : (kennelSelections.find((s) => s.id === editingHeaterId) ?? null);
  const editingOption = editingSelection
    ? kennelSideOptions.find(
        (o) => o.id === editingSelection.kennelSideOptionId
      )
    : null;

  const handleKennelSideOptionChange = (
    selectionId: number,
    optionIdStr: string
  ) => {
    const optionId = parseInt(optionIdStr, 10);
    const option = kennelSideOptions.find((o) => o.id === optionId);
    if (!option) return;

    onChange(
      kennelSelections.map((sel) =>
        sel.id === selectionId
          ? {
              ...sel,
              kennelSideOptionId: option.id,
              kennelSideId: option.kennelSideId,
              size: option.size,
              kennelId: null, // reset specific-kennel pick when type changes
            }
          : sel
      )
    );
  };

  const handleKennelChange = (selectionId: number, kennelId: number) => {
    onChange(
      kennelSelections.map((sel) =>
        sel.id === selectionId ? { ...sel, kennelId } : sel
      )
    );
  };

  const handleAddKennelRow = () => {
    onChange([
      ...kennelSelections,
      {
        id: getNextId(),
        kennelSideOptionId: null,
        kennelSideId: null,
        size: null,
        kennelId: null,
        heaterEnabled: false,
        heaterNights: 0,
      },
    ]);
  };

  const handleRemoveKennelRow = (selectionId: number) => {
    onChange(kennelSelections.filter((s) => s.id !== selectionId));
  };

  // KennelHeaterManager save: writes heaterEnabled + heaterNights back to
  // the row being edited.
  const handleHeaterSave = (useHeater: boolean, nights: number) => {
    if (editingHeaterId === null) return;
    onChange(
      kennelSelections.map((sel) =>
        sel.id === editingHeaterId
          ? {
              ...sel,
              heaterEnabled: useHeater,
              heaterNights: useHeater ? nights : 0,
            }
          : sel
      )
    );
    setEditingHeaterId(null);
  };

  // Auto-pick the first available kennel when only a side option has been
  // chosen. Matches BookingForm's UX (less clicking for the common case).
  React.useEffect(() => {
    kennelSelections.forEach((sel) => {
      if (
        sel.kennelSideOptionId &&
        !sel.kennelId &&
        sel.size !== null &&
        availableKennels.length > 0
      ) {
        const unselected = getFirstAvailableUnselectedKennel(
          sel.kennelSideId,
          sel.size,
          kennelSelections,
          availableKennels,
          sel.id
        );
        if (unselected) {
          handleKennelChange(sel.id, unselected.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kennelSelections, availableKennels]);

  return (
    <Box id={id}>
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
          }}
        >
          <FieldLabel>
            Kennels<span className="required">*</span>
          </FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>

      {/* Section-level error */}
      {error && (
        <FormFieldRow>
          <FieldLabelContainer />
          <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
            <Box
              sx={{
                color: 'error.main',
                fontWeight: 700,
                fontSize: '1.8rem',
                pl: '0.5rem',
              }}
            >
              {error}
            </Box>
          </FieldInputContainer>
        </FormFieldRow>
      )}

      {kennelSelections.map((selection, index) => {
        const kennelsForSelection = getKennelsForSelection(
          selection.kennelSideId,
          selection.size,
          availableKennels
        );
        const selectedKennelIds = getSelectedKennelIds(
          kennelSelections,
          selection.id
        );
        const showAddButton = index === kennelSelections.length - 1;
        const optionHasKennels = selectedOptionHasKennels(selection);
        const currentOption = kennelSideOptions.find(
          (opt) => opt.id === selection.kennelSideOptionId
        );
        const displayName = currentOption?.displayName || '';
        const sidePrefix = getKennelSidePrefix(displayName);
        const optionSupportsHeater = supportsHeater(displayName);

        return (
          <Box
            key={selection.id}
            sx={{
              ...(index > 0 && {
                marginTop: '1rem',
                borderTop: (t) =>
                  `3px dashed ${
                    (t.palette as { dividerContrast?: string })
                      .dividerContrast ?? t.palette.divider
                  }`,
              }),
            }}
          >
            {/* Row 1: Kennel type select (+ remove (x) if not first) */}
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel>
                  {kennelSelections.length === 1 ? '' : `Kennel ${index + 1}`}
                </FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <CustomSelect
                    value={selection.kennelSideOptionId?.toString() || ''}
                    onChange={(value) =>
                      handleKennelSideOptionChange(selection.id, value)
                    }
                    ariaLabel={
                      kennelSelections.length === 1
                        ? 'Kennel type'
                        : `Kennel ${index + 1} type`
                    }
                    label=""
                    placeholder="Select..."
                    disabled={isSubmitting}
                    width="100%"
                    maxWidth="600px"
                  >
                    {kennelSideOptions.map((option) => {
                      const acceptsPets = sideAcceptsPetTypes(
                        option.allowedPetTypeIds,
                        petTypeIds
                      );
                      const optKennelsAvailable =
                        option.size === null
                          ? true
                          : hasAvailableUnselectedKennels(
                              option.kennelSideId,
                              option.size,
                              kennelSelections,
                              availableKennels,
                              selection.id
                            );
                      const displayText = option.displayName.replace(
                        'Cattery Standard',
                        'Cattery'
                      );
                      return (
                        <CustomSelectMenuItem
                          key={option.displayName}
                          value={option.id.toString()}
                          disabled={!optKennelsAvailable || !acceptsPets}
                        >
                          {!acceptsPets
                            ? `${displayText} - not suitable for these pets`
                            : optKennelsAvailable
                              ? displayText
                              : `${displayText} - none available`}
                        </CustomSelectMenuItem>
                      );
                    })}
                  </CustomSelect>

                  {index > 0 && (
                    <StyledIconButton
                      variant="delete"
                      onClick={() => handleRemoveKennelRow(selection.id)}
                      disabled={isSubmitting}
                      sx={{ '& svg': { fontSize: '2.75rem' } }}
                      title="Remove kennel"
                    >
                      <AddIcon sx={{ transform: 'rotate(45deg)' }} />
                    </StyledIconButton>
                  )}
                </Box>
              </FieldInputContainer>
            </FormFieldRow>

            {/* Row 2: Specific kennel select (+ heater pencil if supported) */}
            {selection.kennelSideOptionId && optionHasKennels && (
              <FormFieldRow>
                <FieldLabelContainer />
                <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <CustomSelect
                      key={`kennel-select-${selection.id}-${selection.kennelSideOptionId}`}
                      value={selection.kennelId?.toString() || ''}
                      ariaLabel={
                        kennelSelections.length === 1
                          ? 'Kennel'
                          : `Kennel ${index + 1}`
                      }
                      onChange={(value) =>
                        handleKennelChange(selection.id, parseInt(value, 10))
                      }
                      label=""
                      placeholder="Select..."
                      disabled={
                        isSubmitting || kennelsForSelection.length === 0
                      }
                      width="100%"
                      maxWidth="600px"
                    >
                      {kennelsForSelection.map((kennel) => {
                        const isAlreadySelected = selectedKennelIds.has(
                          kennel.id
                        );
                        return (
                          <CustomSelectMenuItem
                            key={kennel.id}
                            value={kennel.id.toString()}
                            disabled={isAlreadySelected}
                          >
                            {sidePrefix}
                            {kennel.kennelNumber}
                            {isAlreadySelected && ' - already selected'}
                          </CustomSelectMenuItem>
                        );
                      })}
                    </CustomSelect>

                    {optionSupportsHeater && (
                      <StyledIconButton
                        onClick={() => setEditingHeaterId(selection.id)}
                        disabled={isSubmitting || !selection.kennelId}
                        title={
                          selection.heaterEnabled && selection.heaterNights
                            ? `Heater: ${selection.heaterNights} night(s)`
                            : 'Configure heater'
                        }
                        sx={{
                          '& svg': { fontSize: '2.5rem' },
                          opacity:
                            selection.heaterEnabled && selection.heaterNights
                              ? 1
                              : 0.5,
                        }}
                      >
                        {getCustomIcon('heater', '2.4rem')}
                      </StyledIconButton>
                    )}
                  </Box>
                </FieldInputContainer>
              </FormFieldRow>
            )}

            {/* Add-row button on last row - icon-only, right-aligned,
                matching the BookingForm "Add additional kennel" pattern. */}
            {showAddButton && (
              <FormFieldRow paddingBottom={'2rem'}>
                <FieldLabelContainer />
                <FieldInputContainer sx={{ paddingLeft: '1rem' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      maxWidth: '600px',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      mt: 1,
                    }}
                  >
                    <AddRowButton
                      onClick={handleAddKennelRow}
                      disabled={isSubmitting}
                      title="Add additional kennel"
                    />
                  </Box>
                </FieldInputContainer>
              </FormFieldRow>
            )}
          </Box>
        );
      })}

      {/* Per-row heater modal */}
      <ModalBase
        open={editingHeaterId !== null}
        onClose={() => setEditingHeaterId(null)}
        mobileWidth="92vw"
        petName={
          editingSelection && editingOption
            ? `${getKennelSidePrefix(editingOption.displayName)}${
                availableKennels.find((k) => k.id === editingSelection.kennelId)
                  ?.kennelNumber ?? ''
              }`
            : 'Kennel'
        }
        headerIcon={getCustomIcon('heater')}
        headerTitle="Configure Heater"
      >
        {editingSelection && (
          <KennelHeaterManager
            kennelSelectionId={editingSelection.id}
            heaterEnabled={editingSelection.heaterEnabled ?? false}
            heaterNights={editingSelection.heaterNights ?? 0}
            bookingLength={numberOfDays}
            onSave={handleHeaterSave}
            onCancel={() => setEditingHeaterId(null)}
            isLoading={isSubmitting}
          />
        )}
      </ModalBase>
    </Box>
  );
};

export default ConvertKennelsSection;
